import { ChangeDetectionStrategy, Component, computed, forwardRef, inject, input, signal } from '@angular/core';
import { AbstractFormControl } from '../abstract-form-control';
import { AssessmentPresetItemDto, CheckpointService } from '@artemis-software/wr-api';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { watch } from '@/utils/watch';

@Component({
  standalone: false,
  selector: 'wr-assessment-preset-select',
  templateUrl: './assessment-preset-select.component.html',
  styleUrls: ['./assessment-preset-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AssessmentPresetSelectComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssessmentPresetSelectComponent extends AbstractFormControl<AssessmentPresetItemDto> {
  readonly checkpointService = inject(CheckpointService);

  readonly checkpointId = input.required<string>();

  readonly checkpoint = asyncComputed(() => this.checkpointService.findById(this.checkpointId()));

  readonly filteredPresets = computed(() => {
    const checkpoint = this.checkpoint();
    const selectedId = this._value?.id;
    if (!checkpoint) return [];

    return checkpoint.assessmentPresets.filter(p =>
      !p.deprecated || p.id === selectedId,
    );
  });

  readonly visible = signal(true);

  readonly equals = (a?: AssessmentPresetItemDto, b?: AssessmentPresetItemDto): boolean => a?.id === b?.id;

  constructor() {
    super();
    watch(this.checkpoint, () => {
      this.reloadSelect();
    });
  }

  override writeValue(value: AssessmentPresetItemDto | undefined) {
    super.writeValue(value);
    this.reloadSelect();
  }

  override setDisabledState(isDisabled: boolean) {
    super.setDisabledState(isDisabled);
    this.reloadSelect();
  }

  hasEquipment(value: AssessmentPresetItemDto) {
    return value.buildingEquipmentRelated;
  }

  hasSelectedEquipment() {
    return this._value?.buildingEquipmentRelated;
  }

  assessmentPresetText(assessmentPreset: AssessmentPresetItemDto): string {
    if (assessmentPreset.deprecated) {
      return assessmentPreset.text + ' (überholt)';
    }
    return assessmentPreset.text;
  }

  reloadSelect() {
    this.visible.set(false);
    setTimeout(() => {
      this.visible.set(true);
    });
  }
}
